import {
  faChevronDown,
  faChevronUp,
  faEnvelopeOpen,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type MouseEvent, useState } from 'react';
import { IconButton } from '@/components/shared/IconButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { NOTIFICATION_TRIGGER_ID } from '@/lib/constants/notificationTriggers';
import { RESOURCE_TYPE_META } from '@/lib/constants/resourceTypes';
import { avatarColorClass, getCompanyInitials } from '@/lib/utils/avatar';
import { cn } from '@/lib/utils/cn';
import { relativeTime } from '@/lib/utils/formatters';
import {
  getActionLink,
  getDescriptionSegments,
} from '@/lib/utils/notificationHelpers';
import { OrderImportErrors } from './OrderImportErrors';
import type { NotificationItemProps } from './types';

// Certified-materials notifications that carry an optional brand message,
// shown behind a Show/Hide toggle beneath the description.
const MESSAGE_TRIGGERS = new Set([
  'certified-materials-supplier-new-evidence-requested',
  'certified-materials-document-revision-requested',
  'certified-materials-supplier-reminder-sent',
]);

export function NotificationItem({
  notification: n,
  tab,
  onDismiss,
  onReactivate,
  onMarkRead,
  dense = false,
}: NotificationItemProps) {
  const { getTranslation: t } = useTranslator({ page: 'notifications' });
  const [showMessage, setShowMessage] = useState(false);
  const isActivity = tab === 'activity';
  const isDismissed = n.status === 'DISMISSED';
  const isUnread = n.status === 'UNREAD' || n.status === 'PENDING';
  const actionLink = isActivity ? getActionLink(n, t) : null;
  const segments = getDescriptionSegments(n, t);
  const resourceTypeMeta = RESOURCE_TYPE_META[n.resourceType];
  const brandMessage = MESSAGE_TRIGGERS.has(n.notificationTriggerId)
    ? (n.payload?.message as string | undefined)
    : undefined;

  // Clicking anywhere on an unread notification clears it — a notification row
  // has no other click target, so the whole row is the affordance. The explicit
  // mark-read button stays as the keyboard/screen-reader path, which is why the
  // row is a plain div: giving it `role="button"` would nest that button (and
  // Show message) inside a button, which is invalid.
  const canMarkReadOnRowClick = !isActivity && isUnread && !!onMarkRead;
  function handleRowClick(event: MouseEvent<HTMLDivElement>) {
    if (!onMarkRead) return;
    // Never hijack the row's own controls (Show message, the mark-read button,
    // or any link inside the description).
    if ((event.target as HTMLElement).closest('button, a')) return;
    onMarkRead(n.id);
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 transition-colors hover:bg-brand-accent/10',
        dense ? 'px-3 py-2' : 'px-6 py-3',
        isUnread && 'bg-primary/2',
        canMarkReadOnRowClick && 'cursor-pointer',
      )}
      {...(canMarkReadOnRowClick ? { onClick: handleRowClick } : {})}
    >
      {/* Unread dot — left of avatar for notifications tab. Only rendered when
          unread: reserving the column on read rows left a visible gutter of
          dead space down the left edge of the feed. */}
      {!isActivity && isUnread && (
        <div className='flex items-start pt-1.5 shrink-0'>
          <span className='size-2 rounded-full bg-destructive' />
        </div>
      )}

      {/* Avatar — only shown when there's a company image */}
      {n.createdByCompanyImageUrl && (
        <Avatar className='size-8 shrink-0 rounded-full mt-0.5'>
          <AvatarImage
            src={`${n.createdByCompanyImageUrl}?fit=crop&h=64&w=64`}
            alt={n.createdByCompanyName}
            className='object-cover'
          />
          <AvatarFallback
            className={`rounded-full text-xs font-bold ${avatarColorClass(n.createdByCompanyId)}`}
          >
            {getCompanyInitials(n.createdByCompanyName)}
          </AvatarFallback>
        </Avatar>
      )}

      {/* Content */}
      <div className='flex-1 min-w-0'>
        <p className='text-xs leading-snug text-foreground line-clamp-2'>
          {segments.map((seg, i) =>
            seg.href ? (
              <a
                key={i}
                href={seg.href}
                target='_blank'
                rel='noreferrer'
                className='font-medium underline'
              >
                {seg.text}
              </a>
            ) : seg.bold ? (
              <strong key={i}>{seg.text}</strong>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </p>

        {n.notificationTriggerId ===
          NOTIFICATION_TRIGGER_ID.ordersBulkImportCompleted && (
          <OrderImportErrors payload={n.payload} />
        )}

        {n.notificationTriggerId ===
          NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequestReminded && (
          <p className='mt-1 text-xs text-muted-foreground'>
            {t('triggers.supplyChainCollaborationRequestReminded.note')}
          </p>
        )}

        {/* Optional sender message — small Show/Hide link + revealed text */}
        {brandMessage && (
          <>
            <Button
              variant='link'
              size='sm'
              className='h-auto p-0 mt-1 gap-1 text-xs'
              onClick={() => setShowMessage(v => !v)}
            >
              {showMessage
                ? t('actions.hideMessage')
                : t('actions.showMessage')}
              {showMessage ? (
                <FontAwesomeIcon icon={faChevronUp} className='size-3' />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} className='size-3' />
              )}
            </Button>
            {showMessage && (
              <p className='mt-1 text-xs text-muted-foreground whitespace-pre-wrap'>
                {brandMessage}
              </p>
            )}
          </>
        )}

        {/* Badge + time */}
        <div className='flex items-center gap-2 mt-1 flex-wrap'>
          {resourceTypeMeta && (
            <span
              className='text-xs font-medium px-1.5 py-0.5 rounded text-foreground'
              style={{ backgroundColor: `var(${resourceTypeMeta.cssVar})` }}
            >
              {t(resourceTypeMeta.key)}
            </span>
          )}
          <span className='text-xs text-muted-foreground'>
            {relativeTime(n.createdAt, t)}
          </span>
        </div>
      </div>

      {/* Right: mark-as-read (notifications only, unread rows only) */}
      {!isActivity && isUnread && onMarkRead && (
        <div className='flex items-center shrink-0 mt-0.5'>
          <IconButton
            variant='ghost'
            size='icon'
            className='size-6 text-muted-foreground hover:text-primary shrink-0'
            tooltip={t('actions.markRead')}
            onClick={() => onMarkRead(n.id)}
          >
            <FontAwesomeIcon icon={faEnvelopeOpen} className='text-xs' />
          </IconButton>
        </div>
      )}

      {/* Right: action button + dismiss/reactivate (activity only) */}
      {isActivity && (
        <div className='flex items-center gap-1.5 shrink-0 mt-0.5'>
          {!isDismissed && actionLink?.link && (
            <a
              href={actionLink.link}
              target='_self'
              className='inline-flex items-center h-7 px-2.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/80 transition-colors whitespace-nowrap'
            >
              {actionLink.label}
            </a>
          )}
          {isDismissed && (
            <Button
              size='default'
              variant='outline'
              onClick={() => onReactivate(n.id)}
            >
              {t('actions.reactivate')}
            </Button>
          )}
          {!isDismissed && (
            <IconButton
              variant='ghost'
              size='icon'
              className='size-6 text-muted-foreground hover:text-destructive shrink-0'
              tooltip={t('actions.dismiss')}
              onClick={() => onDismiss(n.id)}
            >
              <FontAwesomeIcon icon={faTrash} className='text-xs' />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
}
